import { useState, useRef, useCallback } from "react";
import Vapi from "@vapi-ai/web";

export type CallStatus = "idle" | "connecting" | "active" | "ended";

interface VapiMessage {
  type: string;
  role?: string;
  transcript?: string;
  transcriptType?: string;
}

// Error object structure from VAPI SDK
interface VapiError {
  type?: string;
  stage?: string;
  error?: {
    message?: string;
    name?: string;
    stack?: string;
    [key: string]: unknown;
  };
  message?: string;
  timestamp?: string;
  [key: string]: unknown;
}

// Call start failed event from VAPI SDK
interface VapiCallStartFailedEvent {
  stage: string;
  totalDuration: number;
  error: string;
  errorStack?: string;
  timestamp: string;
  context: Record<string, unknown>;
}

// Voice token response from server
interface VoiceTokenResponse {
  token: string;
  assistantId: string;
  expiresIn: number;
}

interface UseVoiceOptions {
  /** Our internal assistant ID (used to fetch the token) */
  assistantId: string;
  /** Server URL for fetching voice token */
  serverUrl?: string;
  onTranscript?: (
    role: "user" | "assistant",
    text: string,
    isFinal: boolean,
  ) => void;
}

interface UseVoiceReturn {
  callStatus: CallStatus;
  isMuted: boolean;
  volumeLevel: number;
  error: string | null;
  startCall: () => void;
  endCall: () => void;
  toggleMute: () => void;
}

const DEFAULT_SERVER_URL =
  import.meta.env.VITE_SERVER_URL || "http://localhost:3002";

/**
 * Fetch a voice token from the server
 * This token is rate-limited and short-lived for security
 */
async function fetchVoiceToken(
  assistantId: string,
  serverUrl: string,
): Promise<VoiceTokenResponse> {
  const response = await fetch(
    `${serverUrl}/api/public/voice/${assistantId}/token`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    if (response.status === 429) {
      throw new Error(
        errorData.error ||
          "Too many voice call requests. Please try again later.",
      );
    }

    throw new Error(errorData.error || "Failed to start voice call");
  }

  return response.json();
}

/**
 * Hook for managing Vapi voice calls
 *
 * This hook fetches a secure, short-lived token from the server when starting a call.
 * The token is rate-limited (3 calls/hour) and restricted to the specific assistant.
 */
export function useVoice({
  assistantId,
  serverUrl = DEFAULT_SERVER_URL,
  onTranscript,
}: UseVoiceOptions): UseVoiceReturn {
  const [callStatus, setCallStatus] = useState<CallStatus>("idle");
  const [isMuted, setIsMuted] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Store the Vapi instance for the current call
  const vapiRef = useRef<Vapi | null>(null);
  const onTranscriptRef = useRef(onTranscript);

  // Keep onTranscript ref updated
  onTranscriptRef.current = onTranscript;

  /**
   * Set up event listeners on a Vapi instance
   */
  const setupVapiEventListeners = useCallback((vapi: Vapi) => {
    vapi.on("speech-start", () => {
      // Assistant started speaking
    });

    vapi.on("speech-end", () => {
      // Assistant stopped speaking
    });

    vapi.on("call-start", () => {
      console.log("📞 Call started");
      setCallStatus("active");
      setError(null);
    });

    vapi.on("call-end", () => {
      console.log("📞 Call ended");
      setCallStatus("ended");
      setIsMuted(false);
      setVolumeLevel(0);

      // Clean up the Vapi instance
      vapiRef.current = null;

      // Reset to idle after a brief delay
      setTimeout(() => setCallStatus("idle"), 1500);
    });

    vapi.on("volume-level", (volume: number) => {
      setVolumeLevel(volume);
    });

    vapi.on("message", (message: VapiMessage) => {
      console.log("Vapi message:", message);

      // Handle transcript messages
      if (message.type === "transcript" && message.transcript) {
        const role = message.role === "user" ? "user" : "assistant";
        const isFinal = message.transcriptType === "final";
        onTranscriptRef.current?.(role, message.transcript, isFinal);
      }
    });

    // Handle errors - stop the call when errors occur
    vapi.on("error", (err: VapiError) => {
      console.error("Vapi error:", err);

      // Determine error message
      let errorMessage = "An error occurred";

      // Handle permission denied errors
      if (
        err.type === "daily-call-join-error" ||
        err.type === "daily-error" ||
        err.stage === "daily-call-join"
      ) {
        // Check for permission denied specifically
        const errorString = JSON.stringify(err).toLowerCase();
        if (
          errorString.includes("permission") ||
          errorString.includes("notallowed") ||
          errorString.includes("denied") ||
          errorString.includes("microphone")
        ) {
          errorMessage =
            "Microphone access denied. Please allow microphone permission and try again.";
        } else {
          errorMessage = "Failed to connect to voice call. Please try again.";
        }
      } else if (err.error?.message) {
        errorMessage = err.error.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setCallStatus("idle");

      // Stop the call to ensure the AI stops speaking
      vapi.stop();
      vapiRef.current = null;
    });

    // Handle call start failures (includes permission denial)
    vapi.on("call-start-failed", (event: VapiCallStartFailedEvent) => {
      console.error("Vapi call-start-failed:", event);

      let errorMessage = "Failed to start call";

      // Check for permission denied
      const errorString = JSON.stringify(event).toLowerCase();
      if (
        errorString.includes("permission") ||
        errorString.includes("notallowed") ||
        errorString.includes("denied") ||
        errorString.includes("microphone")
      ) {
        errorMessage =
          "Microphone access denied. Please allow microphone permission and try again.";
      } else if (event.error) {
        errorMessage = event.error;
      }

      setError(errorMessage);
      setCallStatus("idle");

      // Stop the call to ensure the AI stops speaking
      vapi.stop();
      vapiRef.current = null;
    });
  }, []);

  /**
   * Start a voice call
   * Fetches a secure token from the server and initiates the call
   */
  const startCall = useCallback(async () => {
    if (!assistantId) {
      setError("Assistant ID is required");
      return;
    }

    // Prevent starting a new call if one is already in progress
    if (vapiRef.current) {
      console.warn("A call is already in progress");
      return;
    }

    try {
      setCallStatus("connecting");
      setError(null);

      // Step 1: Fetch a secure token from the server
      console.log("🔑 Fetching voice token...");
      const { token, assistantId: externalAssistantId } = await fetchVoiceToken(
        assistantId,
        serverUrl,
      );

      // Step 2: Create a new Vapi instance with the token
      console.log("🎙️ Initializing voice call...");
      const vapi = new Vapi(token);
      vapiRef.current = vapi;

      // Step 3: Set up event listeners
      setupVapiEventListeners(vapi);

      // Step 4: Start the call with the external assistant ID
      // maxDurationSeconds caps call to 10 minutes to prevent billing abuse
      console.log("📞 Starting call...");
      await vapi.start(externalAssistantId, {
        maxDurationSeconds: 600,
      });
    } catch (err) {
      console.error("Error starting call:", err);
      setError(err instanceof Error ? err.message : "Failed to start call");
      setCallStatus("idle");

      // Clean up if we created a Vapi instance
      if (vapiRef.current) {
        vapiRef.current.stop();
        vapiRef.current = null;
      }
    }
  }, [assistantId, serverUrl, setupVapiEventListeners]);

  /**
   * End the current voice call
   */
  const endCall = useCallback(() => {
    if (!vapiRef.current) return;
    vapiRef.current.stop();
    vapiRef.current = null;
  }, []);

  /**
   * Toggle microphone mute state
   */
  const toggleMute = useCallback(() => {
    if (!vapiRef.current) return;
    const newMuteState = !isMuted;
    vapiRef.current.setMuted(newMuteState);
    setIsMuted(newMuteState);
  }, [isMuted]);

  return {
    callStatus,
    isMuted,
    volumeLevel,
    error,
    startCall,
    endCall,
    toggleMute,
  };
}
